import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  Layers,
  LayoutDashboard,
  MessageSquare,
  Server,
  Shield,
  Users,
} from 'lucide-react';
import { useRevealOnScroll } from '../../../hooks/useRevealOnScroll';
import { S9_RAO } from '../../landing-templates/t04-dala-orbit/t04LandingShared';
import {
  ADDON_SEAT_FEE,
  formatEur,
  MAX_SEATS_PER_INSTANCE,
  ORBIS_PORTAL_FEATURES,
  PRODUCT_NAME_MARKETING,
  SETUP_FEE,
  TARIF_ENTERPRISE,
} from '../../../constants';
import { T05ProductShell } from './T05ProductShell';
import { T05ReferoFeatureRow } from './T05ReferoFeatureRow';
import { T05PricingSection } from '../../landing-templates/t05-launchdarkly/T05PricingSection';
import { T05HeroLeadForm } from '../../landing-templates/t05-launchdarkly/T05HeroLeadForm';
import { T05LeadModal } from '../../landing-templates/t05-launchdarkly/T05LeadModal';
import { buildOrbisPricingPlans } from '../../landing-templates/t05-launchdarkly/t05PricingPlans';
import { useT05ProductLead } from '../../../lib/t05ProductLead';
import type { T05ProductPageProps } from './routes.fragment';
import './t05-product.css';

const ORBIS_LEAD = { interest: 'Orbis – Demo', quelle: 't05-product-orbis' };

const LITIGATION_NACHKAUF = 99;

const ORBIS_NAV = [
  { href: '#tarife', label: 'Tarife' },
  { href: '#bausteine', label: 'Bausteine' },
  { href: '#preise', label: 'Preise' },
];

const PORTAL_MANDATES = [
  { name: 'Müller / Schadenersatz', status: 'Aktiv' },
  { name: 'Huber / Kündigung MRG', status: 'Frist' },
  { name: 'Braun / Vertrag', status: 'Entwurf' },
];

interface ProductOrbisProps extends T05ProductPageProps {}

function OrbisDashboardPanel() {
  return (
    <div className="t05-product-panel" aria-label="Orbis Dashboard — Platzhalter">
      <div className="t05-product-panel__bar" aria-hidden>
        <span className="t05-product-panel__dot" />
        <span className="t05-product-panel__dot" />
        <span className="t05-product-panel__dot" />
      </div>
      <p className="t05-product-panel__title">orbis://portal/dashboard</p>
      <div className="t05-product-panel__grid">
        {ORBIS_PORTAL_FEATURES.slice(0, 5).map((f) => (
          <div key={f.id} className="t05-product-panel__row">
            <span>{f.name}</span>
            <span className="t05-product-panel__badge">{f.tag}</span>
            <span className="t05-product-panel__status" aria-hidden />
          </div>
        ))}
      </div>
    </div>
  );
}

function OrbisPortalScreen() {
  return (
    <div className="t05-prod-screen" aria-label="Kanzlei-Portal — Platzhalter">
      <div className="t05-prod-screen__chrome">
        <div className="t05-prod-screen__dots" aria-hidden>
          <span />
          <span />
          <span />
        </div>
        <span className="t05-prod-screen__url">portal://mandate/uebersicht</span>
      </div>
      <div className="t05-prod-screen__body">
        <div className="t05-prod-screen__row">
          <span>Mandatsübersicht</span>
          <span className="t05-prod-screen__badge">3 aktiv</span>
        </div>
        <div className="t05-product-panel__grid">
          {PORTAL_MANDATES.map((m) => (
            <div key={m.name} className="t05-product-panel__row">
              <span>{m.name}</span>
              <span className="t05-product-panel__badge">{m.status}</span>
            </div>
          ))}
        </div>
        <div className="t05-prod-screen__row" style={{ marginTop: '0.75rem' }}>
          <span>Sitze: 4 / 6 belegt</span>
          <Users size={14} aria-hidden style={{ opacity: 0.6 }} />
        </div>
      </div>
    </div>
  );
}

function OrbisArkoScreen() {
  return (
    <div className="t05-prod-screen" aria-label="ARKO im Portal — Platzhalter">
      <div className="t05-prod-screen__chrome">
        <div className="t05-prod-screen__dots" aria-hidden>
          <span />
          <span />
          <span />
        </div>
        <span className="t05-prod-screen__url">arko://session/mandat</span>
      </div>
      <div className="t05-prod-screen__body">
        <div className="t05-prod-screen__row">
          <span>Aktenkontext: Müller / Schadenersatz</span>
          <span className="t05-prod-screen__badge">Edge</span>
        </div>
        <div className="t05-prod-screen__chat-bubble t05-prod-screen__chat-bubble--user">
          Welche Verjährungsfrist gilt nach § 1489 ABGB?
        </div>
        <div className="t05-prod-screen__chat-bubble t05-prod-screen__chat-bubble--arko">
          <strong style={{ color: 'var(--t05-paper)' }}>§ 1489 ABGB</strong> — Drei Jahre ab Kenntnis.
          <br />
          <span className="t05-product-panel__badge" style={{ marginTop: '0.5rem', display: 'inline-block' }}>
            LEXCORE · RIS
          </span>
        </div>
      </div>
    </div>
  );
}

function OrbisSetupScreen() {
  return (
    <div className="t05-prod-screen" aria-label="Orbis Setup — Platzhalter">
      <div className="t05-prod-screen__chrome">
        <div className="t05-prod-screen__dots" aria-hidden>
          <span />
          <span />
          <span />
        </div>
        <span className="t05-prod-screen__url">orbis://setup/edge</span>
      </div>
      <div className="t05-prod-screen__body">
        <div className="t05-product-panel__grid">
          {[
            { label: 'Dedizierter GEX44', badge: 'EU Frankfurt' },
            { label: 'Docker-Stack', badge: 'Self-Hosted' },
            { label: 'Portal + ARKO', badge: 'Ein Login' },
            { label: `Setup ${formatEur(SETUP_FEE)}`, badge: 'Einmalig' },
          ].map((step) => (
            <div key={step.label} className="t05-product-panel__row">
              <span>{step.label}</span>
              <span className="t05-product-panel__badge">{step.badge}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export const ProductOrbis: React.FC<ProductOrbisProps> = ({ onLoginClick, onLeadSubmit }) => {
  useRevealOnScroll();

  const lead = useT05ProductLead({ onLeadSubmit }, ORBIS_LEAD);
  const openDemo = () => lead.openDemo(ORBIS_LEAD.interest);

  const portalFeature = ORBIS_PORTAL_FEATURES.find((f) => f.id === 'portal');
  const arkoFeature = ORBIS_PORTAL_FEATURES.find((f) => f.id === 'arko');

  return (
    <T05ProductShell
      productLabel="Orbis"
      navAnchors={ORBIS_NAV}
      onLoginClick={onLoginClick}
      onDemoClick={openDemo}
    >
      <header className="t05-hero t05-hero--product t05-hero--compact">
        <p className="t05-eyebrow reveal-up">Kanzlei-Plattform · Self-Hosted · EU</p>
        <h1 className="t05-headline t05-headline--product reveal-up">
          Orbis.
          <span className="t05-headline__accent">Ihr Server.</span>
        </h1>
        <p className="t05-lead reveal-up">
          {PRODUCT_NAME_MARKETING} ist die ARKORBIT Kanzlei-Plattform — Self-Hosted auf Ihrem dedizierten
          Server in der EU. <strong>Ein Login. Ihre Akten. Ihre KI.</strong>
        </p>
        <T05HeroLeadForm lead={lead} />
      </header>

      <section className="t05-trust-strip t05-container reveal-up" aria-label="Vertrauensmerkmale">
        <div className="t05-trust-strip__row">
          {['EU-Server Frankfurt', 'Self-Hosted', `${S9_RAO} · Verschwiegenheit`, 'Ein Login'].map((fact, i) => (
            <React.Fragment key={fact}>
              {i > 0 && <span className="t05-trust-strip__sep" aria-hidden>·</span>}
              <span className="t05-trust-strip__item">{fact}</span>
            </React.Fragment>
          ))}
        </div>
      </section>

      <T05PricingSection
        id="tarife"
        kicker="Tarife"
        title="Professional, Premium, Enterprise"
        subtitle={`Alle Beträge EUR zzgl. USt. Setup einmalig ${formatEur(SETUP_FEE)} je Instanz.`}
        plans={buildOrbisPricingPlans({ onDemo: openDemo })}
        footnote="¹ Kein erneutes Setup bei Upgrade Professional → Premium auf derselben Infrastruktur."
      />

      <section className="t05-prod-section t05-container" id="preise" aria-labelledby="orbis-preise-heading">
        <div className="t05-section__header reveal-up">
          <p className="t05-kicker">Zusatzleistungen</p>
          <h2 className="t05-title" id="orbis-preise-heading">
            Sitze &amp; Nachkauf
          </h2>
        </div>

        <div className="t05-prod-addons reveal-up">
          <div className="t05-prod-addon">
            <span className="t05-prod-addon__price">{formatEur(ADDON_SEAT_FEE)}/Mo</span>
            <div>
              <p className="t05-prod-addon__title">Zusatzsitz</p>
              <p className="t05-prod-addon__desc">
                {formatEur(ADDON_SEAT_FEE)} pro Monat und Sitz — bis maximal {MAX_SEATS_PER_INSTANCE} Sitze
                pro Instanz. Darüber hinaus nur {TARIF_ENTERPRISE}.
              </p>
            </div>
          </div>
          <div className="t05-prod-addon">
            <span className="t05-prod-addon__price">{formatEur(LITIGATION_NACHKAUF)}</span>
            <div>
              <p className="t05-prod-addon__title">Litigation-Nachkauf (Premium)</p>
              <p className="t05-prod-addon__desc">
                10 zusätzliche Analysen = {formatEur(LITIGATION_NACHKAUF)} pro Paket.
                Rollforward bis 24 Monate.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="t05-container t05-refero-features" aria-label="Orbis Bausteine">
        <T05ReferoFeatureRow
          id="bausteine"
          accent="coral"
          label="Lego-Prinzip"
          labelIcon={Layers}
          title="Bausteine einer Plattform"
          description="Referenz + Edge — Portal, Diktat, ARKO, OnlyOffice, Nextcloud, Matrix — integriert, nicht lose Fremdtools. LEXCORE/BRAIN zentral bei ARKORBIT, Mandantendaten nur auf Ihrem Edge."
          bullets={[
            { icon: Check, text: 'Ein Login für Dashboard, Akte und KI' },
            { icon: Shield, text: 'Kein Mandanten-PII auf dem ARKORBIT-Firmen-Server' },
            { icon: Layers, text: 'Bausteine als integrierte Plattform' },
          ]}
          footer={
            <Link to="/produkt/arko" className="t05-stack-item__cta">
              ARKO entdecken <ArrowRight size={16} aria-hidden />
            </Link>
          }
          visual={<OrbisDashboardPanel />}
        />

        <T05ReferoFeatureRow
          reverse
          accent="voltage"
          label="Kanzlei-Portal"
          labelIcon={LayoutDashboard}
          title="Kanzlei-Portal"
          description="Dashboard, Mandatsübersicht, Sitzverwaltung — ein Web-Zugang zu allen Diensten."
          bullets={[
            { icon: Check, text: portalFeature?.tag ?? 'Ein Login' },
            { icon: LayoutDashboard, text: portalFeature?.desc ?? 'Ihre Kanzlei an einem Ort, auf Ihrem eigenen Server.' },
          ]}
          visual={<OrbisPortalScreen />}
        />

        <T05ReferoFeatureRow
          accent="gold"
          label="ARKO"
          labelIcon={MessageSquare}
          title="ARKO"
          description="KI-Rechtsassistent mit Mandatskontext und quellenbelegten Antworten aus LEXCORE."
          bullets={[
            { icon: MessageSquare, text: arkoFeature?.tag ?? 'Chat mit Mandatskontext' },
            { icon: Check, text: arkoFeature?.desc ?? 'Rechtsnaher Assistent direkt im Portal — kein Ersatz für die anwaltliche Prüfung.' },
          ]}
          visual={<OrbisArkoScreen />}
        />

        <T05ReferoFeatureRow
          reverse
          accent="trust"
          label="Setup & Infrastruktur"
          labelIcon={Server}
          title="Setup & Infrastruktur"
          description={`Einrichtung Ihrer dedizierten Orbis-Umgebung — einmalig ${formatEur(SETUP_FEE)} je Instanz.`}
          bullets={[
            { icon: Server, text: `Setup einmalig ${formatEur(SETUP_FEE)} je Instanz` },
            { icon: Layers, text: 'Dedizierte Orbis-Umgebung auf Ihrem Edge-Server' },
          ]}
          visual={<OrbisSetupScreen />}
        />
      </section>

      <section className="t05-final-cta t05-container reveal-up">
        <div className="t05-final-cta__band">
          <div className="t05-final-cta__inner">
            <p className="t05-kicker">Einstieg</p>
            <h2 className="t05-title">Orbis auf Ihrem Server</h2>
            <p className="t05-subtitle">
              Demo, Setup und Live-Betrieb — ohne US-Cloud, mit nachvollziehbarem Rechtskontext.
            </p>
            <div className="t05-cta-actions">
              <button type="button" className="t05-btn t05-btn--primary" onClick={openDemo}>
                Kostenlose Analyse
              </button>
              <Link to="/produkt/arko" className="t05-btn t05-btn--ghost">
                ARKO ansehen
              </Link>
            </div>
          </div>
        </div>
        <p className="t05-prod-footnote">
          Orbis – Markenanmeldung ausstehend (kein ®) ·{' '}
          <Link to="/recht">{S9_RAO} &amp; Rechtliches</Link>
        </p>
      </section>
      <T05LeadModal lead={lead} />
    </T05ProductShell>
  );
};
